/* lf-cart-toast — added-to-cart confirmation for a store with no cart drawer.

   Listens for the cart update Horizon's <product-form-component> emits after
   its /cart/add.js POST, so no request is made here and the Klaviyo
   product_added_to_cart pixel path is untouched.

   Two trigger surfaces, both of them a deliberate opt-in rather than "any cart
   mutation": a product card's Add To Cart, and the PDP buy box. Everything else
   that dispatches this event — quantity steppers and line removals on the cart
   page above all — is filtered out twice over, by the `add` action check and by
   the scope check.

   A module, not a plain script, because the event name lives in Shopify's
   storefront standard-events bundle and importing it beats hardcoding the
   string. Module semantics also give us the singleton for free — the browser
   evaluates one URL once, however many section templates include the tag.

   The toast is created here and appended to document.body at open time. It
   must never be rendered inside a .shopify-section: a section wrapper, or any
   transformed ancestor, becomes the containing block for position: fixed and
   would pin it to the section rather than the viewport. */

import { StandardEvents } from '@shopify/events';

const AUTO_DISMISS_MS = 6000;

/* The two places an add is worth confirming. A card anywhere on the site, and
   the PDP buy box (section lf-product-main). */
const SCOPES = '.lf-product-card, [data-lf-product-main]';

/* Shopify's placeholder for a product with no real options. */
const NO_VARIANT = 'Default Title';

const CLOSE_ICON =
  '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M1.5 1.5l9 9M10.5 1.5l-9 9"/></svg>';

const DEFAULTS = {
  cartUrl: '/cart',
  status: 'Added to cart',
  linkLabel: 'View Cart',
  closeLabel: 'Dismiss',
  announceSuffix: 'added to cart',
};

let config = null;
let toast = null;
let refs = null;
let announcer = null;
let dismissTimer = null;
let closeTimer = null;
let paused = false;

const readConfig = () => {
  if (config) return config;

  config = { ...DEFAULTS };

  const node = document.querySelector('script[type="application/json"][data-lf-cart-toast-config]');
  if (!node) return config;

  try {
    Object.assign(config, JSON.parse(node.textContent || '{}'));
  } catch (error) {
    // Malformed config: the defaults above still produce a usable toast.
  }

  return config;
};

const build = () => {
  if (toast) return;

  const settings = readConfig();

  toast = document.createElement('div');
  toast.className = 'lf-cart-toast';
  toast.hidden = true;
  toast.innerHTML = `
    <div class="lf-cart-toast__panel">
      <span class="lf-cart-toast__frame"><img class="lf-cart-toast__thumb" alt="" width="56" height="56"></span>
      <div class="lf-cart-toast__body">
        <p class="lf-cart-toast__status"></p>
        <p class="lf-cart-toast__name"></p>
        <a class="lf-text-btn lf-cart-toast__link"></a>
      </div>
      <button type="button" class="lf-cart-toast__close">${CLOSE_ICON}</button>
    </div>`;

  refs = {
    frame: toast.querySelector('.lf-cart-toast__frame'),
    thumb: toast.querySelector('.lf-cart-toast__thumb'),
    status: toast.querySelector('.lf-cart-toast__status'),
    name: toast.querySelector('.lf-cart-toast__name'),
    link: toast.querySelector('.lf-cart-toast__link'),
    close: toast.querySelector('.lf-cart-toast__close'),
  };

  refs.status.textContent = settings.status;
  refs.link.textContent = settings.linkLabel;
  refs.link.href = settings.cartUrl;
  refs.close.setAttribute('aria-label', settings.closeLabel);

  refs.close.addEventListener('click', close);

  /* Reading the confirmation is the whole job, so the countdown stops while a
     pointer or the keyboard is inside the panel. */
  toast.addEventListener('mouseenter', pause);
  toast.addEventListener('mouseleave', resume);
  toast.addEventListener('focusin', pause);
  toast.addEventListener('focusout', resume);

  /* Announcements go through a separate always-present live region: a node
     that is display:none at the moment its text changes announces nothing. */
  announcer = document.createElement('div');
  announcer.className = 'visually-hidden';
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('role', 'status');
};

const pause = () => {
  paused = true;
  window.clearTimeout(dismissTimer);
};

const resume = () => {
  paused = false;
  scheduleDismiss();
};

const scheduleDismiss = () => {
  window.clearTimeout(dismissTimer);
  if (paused) return;
  dismissTimer = window.setTimeout(close, AUTO_DISMISS_MS);
};

const onKeydown = (event) => {
  if (event.key === 'Escape') close();
};

/* The cart line the add produced, when it can be identified. It is the only
   source that knows which VARIANT went in — the PDP heading is the product
   name alone, and the size chips are a custom picker the cart never sees. */
const lineTitle = (line) => {
  if (!line) return '';

  const product = (line.product_title || '').trim();
  const variant = (line.variant_title || '').trim();

  if (!product) return (line.title || '').trim();
  if (!variant || variant === NO_VARIANT) return product;

  return `${product} · ${variant}`;
};

/* Preferred over the cart line's image: the photo on screen is already decoded
   and cached, so the toast costs no request. */
const scopeImage = (scope) => {
  const image = scope.querySelector('.lf-product-card__img, .lf-product-main__stage-img');
  return image instanceof HTMLImageElement ? image.currentSrc || image.src : '';
};

/* Fallback only (a placeholder-SVG card, a PDP with no media). The Ajax cart
   hands back the full-size original, so the CDN is asked for a thumb rather
   than a 2000px file being pulled into a 56px frame. */
const lineImage = (line) => {
  const url = line && typeof line.image === 'string' ? line.image : '';
  if (!url) return '';
  return url.includes('?') ? `${url}&width=112` : `${url}?width=112`;
};

const open = (scope, line) => {
  build();

  // Portalled here, not in Liquid — see the note at the top of this file.
  if (toast.parentNode !== document.body) document.body.append(toast);
  if (announcer.parentNode !== document.body) document.body.append(announcer);

  const title =
    lineTitle(line) ||
    scope.querySelector('.lf-product-card__name-link, .lf-product-main__title')?.textContent?.trim() ||
    '';
  const src = scopeImage(scope) || lineImage(line);

  refs.name.textContent = title;

  if (src) {
    refs.thumb.src = src;
    refs.thumb.alt = '';
    refs.frame.hidden = false;
  } else {
    // Placeholder-SVG card: no photo to show, so the slot collapses.
    refs.frame.hidden = true;
  }

  window.clearTimeout(closeTimer);
  toast.hidden = false;

  // Two frames: one for the un-hide to paint, one for the transition to run
  // from the parked transform rather than skipping straight to the end state.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add('is-open'));
  });

  announcer.textContent = title
    ? `${title} ${readConfig().announceSuffix}`
    : readConfig().status;

  document.addEventListener('keydown', onKeydown);
  paused = false;
  scheduleDismiss();
};

const close = () => {
  if (!toast || toast.hidden) return;

  window.clearTimeout(dismissTimer);
  document.removeEventListener('keydown', onKeydown);
  toast.classList.remove('is-open');

  // Timeout rather than transitionend: prefers-reduced-motion can leave nothing
  // to transition, and a never-fired event would strand the node visible.
  closeTimer = window.setTimeout(() => {
    toast.hidden = true;
    announcer.textContent = '';
  }, 400);
};

/* Dispatched as a GID (gid://shopify/ProductVariant/123); the Ajax cart reports
   plain numeric ids, so the tail is what matches. */
const addedVariantId = (event) => {
  const lines = Array.isArray(event.lines) ? event.lines : [];
  const id = lines[0]?.merchandiseId;
  if (typeof id !== 'string' && typeof id !== 'number') return '';
  return String(id).split('/').pop();
};

const findLine = (items, variantId) => {
  if (!Array.isArray(items) || !variantId) return null;
  return items.find((item) => String(item?.variant_id ?? item?.id) === variantId) || null;
};

const onCartUpdate = (event) => {
  // Removals and quantity edits carry the same event name.
  if (event.action !== 'add') return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const scope = target.closest(SCOPES);
  if (!scope) return; // every other add path opts out

  const variantId = addedVariantId(event);

  event.promise
    ?.then((result) => {
      if (result?.detail?.didError) return;
      // Null when the cart payload cannot be matched; open() falls back to the
      // product name and photo already on screen.
      open(scope, findLine(result?.detail?.items, variantId));
    })
    .catch(() => {
      // A rejected add already surfaces its own error message on the form.
    });
};

document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
